import React, { useState } from "react";

type Category = "ALL" | "PROGRAMS" | "BOOKS" | "TOOLS" | "FREE";

interface Product {
  label: string;
  title: string;
  price: string;
  description: string;
  href: string;
  category: Exclude<Category, "ALL">;
  image: string;
  alt: string;
}

const PRODUCTS: Product[] = [
  {
    label: "RESET",
    title: "Identity Reset",
    price: "$47",
    description: "Renew the beliefs and labels quietly shaping your choices.",
    href: "https://store.iamieshiadanielle.com/product-details/product/6a9990d12ec6f6c3e6a0fe7f",
    category: "PROGRAMS",
    image:
      "https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946d61a90361de8b527d3e.jpg",
    alt: "Identity Reset workshop by Ieshia Danielle",
  },
  {
    label: "WORKBOOK",
    title: "From Burnout to Becoming Workbook",
    price: "$27",
    description: "Name your burnout source and write a practical plan forward.",
    href: "https://iamieshiadanielle.com/programs/workbook",
    category: "BOOKS",
    image:
      "https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946d61a90361de8b527d35.jpg",
    alt: "From Burnout to Becoming Workbook by Ieshia Danielle",
  },
  {
    label: "BUNDLE",
    title: "Identity Reset + Workbook Bundle",
    price: "$55",
    description:
      "Save when you bundle the Identity Reset workshop with the workbook.",
    href: "https://store.iamieshiadanielle.com/product-details/product/6a98cd6f86a8dfe2ef9d9ef1/?variant=6a98cd6f86a8dfe2ef9d9ef9",
    category: "PROGRAMS",
    image:
      "https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a957aeff22eac14d9b5abff.jpg",
    alt: "Identity Reset and From Burnout to Becoming workbook bundle by Ieshia Danielle",
  },
  {
    label: "BOOK",
    title: "When God Says Let Go",
    price: "$20",
    description:
      "A faith-centered guide for releasing what God is asking you to leave.",
    href: "https://store.iamieshiadanielle.com/product-details/product/when-god-says-let-go-guided-workbook-by-ieshia-danielle",
    category: "BOOKS",
    image:
      "https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946d610914f11215207638.jpg",
    alt: "When God Says Let Go by Ieshia Danielle",
  },
  {
    label: "APP",
    title: "Bible Babes Budgeting App",
    price: "$15/mo",
    description: "Build financial stewardship into your everyday life.",
    href: "https://store.iamieshiadanielle.com/product-details/product/bible-babes-budgeting-app",
    category: "TOOLS",
    image:
      "https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946d61a90361de8b527d47.jpg",
    alt: "Bible Babes Budgeting App by Ieshia Danielle",
  },
  {
    label: "FREE",
    title: "Biblical Affirmations",
    price: "FREE",
    description: "Renew your mind with truth you can return to every day.",
    href: "https://store.iamieshiadanielle.com/product-details/product/free-christian-affirmations-2026-speak-life-daily",
    category: "FREE",
    image:
      "https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946d61934a62aec8961e8a.jpg",
    alt: "Biblical Affirmations by Ieshia Danielle",
  },
  {
    label: "GUIDE",
    title: "Don't Marry That Man",
    price: "FREE",
    description: "A discernment guide for the woman who needs clarity.",
    href: "https://store.iamieshiadanielle.com/product-details/product/don-t-marry-that-man-free-christian-discernment-guide",
    category: "FREE",
    image:
      "https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946d61934a62aec8961e93.jpg",
    alt: "Don't Marry That Man by Ieshia Danielle",
  },
];

const FILTERS: Category[] = ["ALL", "PROGRAMS", "BOOKS", "TOOLS", "FREE"];

export const ShopProducts = () => {
  const [activeFilter, setActiveFilter] = useState<Category>("ALL");

  const filteredProducts =
    activeFilter === "ALL"
      ? PRODUCTS
      : PRODUCTS.filter((p) => p.category === activeFilter);

  return (
    <section className="bg-white w-full py-16 md:py-24">
      <div className="w-full px-4 sm:px-8 lg:px-16 space-y-10">
        {/* Filter pills */}
        <div className="flex flex-wrap gap-3">
          {FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setActiveFilter(filter)}
              className={`text-[11px] font-bold uppercase tracking-widest px-5 py-2 rounded-full border transition-all ${
                activeFilter === filter
                  ? "bg-[#4A0E26] border-[#4A0E26] text-white"
                  : "bg-[#FAF6F0] border-[#E8DEC8] text-[#380C1E]/70 hover:border-[#9E1B50] hover:text-[#9E1B50]"
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Products grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {filteredProducts.map((product) => (
            <a
              key={product.title}
              href={product.href}
              target={product.href.startsWith("http") ? "_blank" : undefined}
              rel={
                product.href.startsWith("http")
                  ? "noopener noreferrer"
                  : undefined
              }
              className="group block bg-[#FAF6F0] overflow-hidden hover:bg-[#F3E9DA] transition-colors"
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={product.image}
                  alt={product.alt}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 lg:p-8 space-y-1">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-serif text-xl md:text-2xl font-normal text-[#380C1E]">
                    {product.title}
                  </h3>
                  <span className="font-serif text-lg font-semibold text-[#380C1E] whitespace-nowrap">
                    {product.price}
                  </span>
                </div>
                <p className="text-sm text-[#380C1E]/70 font-light leading-relaxed max-w-sm pt-1">
                  {product.description}
                </p>
                <span className="mt-3 inline-flex items-center justify-center gap-1.5 bg-[#4A0E26] hover:bg-[#9E1B50] text-white font-bold uppercase text-[11px] tracking-widest px-6 py-3 rounded-full transition-all w-full sm:w-auto">
                  Add To Cart
                  <span aria-hidden="true">→</span>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
